import { apiTimeOffPolicy } from "@/api/time-off.api.ts";
import { useModals, TagVariants, TagAppearance } from "@skailer-com/uikit";
import { TimeOffRequestViewModal } from "@/components/time-off/widgets/TimeOffRequestViewModal.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { EmployeeEditTimeOffBalanceModal } from "@/pages/employees/employee/widgets/EmployeeEditTimeOffBalanceModal";
import { ApiApprovalRequestStatus } from "@/types/approvals/approval.types.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import {
  ApiTimeOffAdjustmentPayload,
  ApiTimeOffLogType,
  getMockTimeOffAdjustment,
} from "@/types/time-off/time-off.types.ts";
import { useTranslation } from "react-i18next";

export default function useTimeOffHook() {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { handleErrors } = useErrorHandle();

  const openViewRequest = async (requestId: number) => {
    openModal(TimeOffRequestViewModal, { requestId });
  };

  const openEditBalance = async (adjustmentId?: number) => {
    if (!adjustmentId) return;

    const { data: log, error } = await apiTimeOffPolicy.getAdjustmentById(adjustmentId);

    if (!log) return;

    const adjustmentPayload: ApiTimeOffAdjustmentPayload = {
      balance: parseFloat(log.balance),
      date_expire: log.date_expire,
      category_id: log.category.id,
      employee_id: log.employee.id,
      date_adjustment: log.date_adjustment,
      comment: log.comment,
    };

    if (error) handleErrors(error);

    openModal(EmployeeEditTimeOffBalanceModal, {
      adjustment: adjustmentPayload,
      adjustmentId: adjustmentId,
    });
  };

  const openAdjustmentBalance = (employee: ApiEmployeeShort) => {
    if (!employee) return;

    const adjustment = getMockTimeOffAdjustment(employee);

    openModal(EmployeeEditTimeOffBalanceModal, {
      adjustment: adjustment,
    });
  };

  const getTimeOffRequestStatusColor = (status: ApiApprovalRequestStatus) => {
    let textColor = "";
    let tagVariant: TagVariants = "primary";
    const tagAppearance: TagAppearance = "filled";

    switch (status) {
      case ApiApprovalRequestStatus.approved:
        break;
      case ApiApprovalRequestStatus.awaiting:
        tagVariant = "new";
        textColor = "text-new";
        break;
      case ApiApprovalRequestStatus.canceled:
        tagVariant = "secondary";
        textColor = "text-secondary";
        break;
      case ApiApprovalRequestStatus.rejected:
        tagVariant = "red";
        textColor = "text-red";
        break;
    }

    return { tagVariant, textColor, tagAppearance };
  };

  const getActionTypeTag = (status: ApiTimeOffLogType) => {
    const textColor = "";
    let tagVariant: TagVariants = "primary";
    const tagAppearance: TagAppearance = "outline";

    switch (status) {
      case ApiTimeOffLogType.accrual:
        break;
      case ApiTimeOffLogType.adjustment:
        tagVariant = "warning";
        break;
      case ApiTimeOffLogType.initial_balance:
      case ApiTimeOffLogType.balance_import:
        tagVariant = "secondary";
        break;
      case ApiTimeOffLogType.carryover:
        tagVariant = "positive";
        break;
      case ApiTimeOffLogType.canceled:
      case ApiTimeOffLogType.expire:
      case ApiTimeOffLogType.expiration:
        tagVariant = "red";
        break;
    }

    return { tagVariant, textColor, tagAppearance };
  };

  const getActionTypeTagTitle = (status: ApiTimeOffLogType) => {
    const statusNames: Record<ApiTimeOffLogType, string> = {
      [ApiTimeOffLogType.accrual]: t("time_off:accrued"),
      [ApiTimeOffLogType.carryover]: t("time_off:action_tag_carryover"),
      [ApiTimeOffLogType.initial_balance]: t("time_off:accrual_action_initial_balance"),
      [ApiTimeOffLogType.balance_import]: t("time_off:accrual_action_balance_import"),
      [ApiTimeOffLogType.used]: t("time_off:accrual_action_used"),
      [ApiTimeOffLogType.adjustment]: t("time_off:action_tag_adjusted"),
      [ApiTimeOffLogType.expire]: t("time_off:action_tag_burned"),
      [ApiTimeOffLogType.expiration]: t("time_off:action_tag_burned"),
      [ApiTimeOffLogType.canceled]: t("time_off:accrual_action_canceled"),
    };

    return statusNames[status];
  };

  const getActionTypeTitle = (status: ApiTimeOffLogType) => {
    const statusNames: Record<ApiTimeOffLogType, string> = {
      [ApiTimeOffLogType.accrual]: t("time_off:accrual_action_accrual"),
      [ApiTimeOffLogType.carryover]: t("time_off:accrual_action_carryover"),
      [ApiTimeOffLogType.initial_balance]: t("time_off:accrual_action_initial_balance"),
      [ApiTimeOffLogType.balance_import]: t("time_off:accrual_action_balance_import"),
      [ApiTimeOffLogType.used]: t("time_off:accrual_action_used"),
      [ApiTimeOffLogType.adjustment]: t("time_off:accrual_action_adjustment"),
      [ApiTimeOffLogType.expire]: t("time_off:accrual_action_expire"),
      [ApiTimeOffLogType.expiration]: t("time_off:accrual_action_expiration"),
      [ApiTimeOffLogType.canceled]: t("time_off:accrual_action_canceled"),
    };

    return statusNames[status];
  };

  return {
    openViewRequest,
    openEditBalance,
    openAdjustmentBalance,
    getTimeOffRequestStatusColor,
    getActionTypeTitle,
    getActionTypeTagTitle,
    getActionTypeTag,
  };
}
